import moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs';

import { DATE_FORMAT_SAP, DATE_FORMAT_SHAREPOINT } from '@shared-constants/date-format-constants';
import { CatsNoBillCodeDescriptionEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { arrayIsTruthy, findUnique } from '@shared-helpers/array-object-helpers';
import { addDecimals, safeCompare, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import { IExtDocument } from '@shared-interfaces/documents';

import { ICatRecord } from '../interfaces/incoming';
import { ITableConfigEntity } from '../interfaces/internal-interfaces';
import { Employee } from '../scripts/employee';
import { DatahubService } from '../services/datahub.service';
import { ModalService } from '../services/modal.service';
import { BasicInterface, IAnyInterfaceType } from './basicInterface';
import { ContractHandler } from './contracts/contract-handler';
import { Equipment } from './equipment';
import { ExtDocument } from './extDocument';
import { ProductionGroup } from './Production-Group/ProductionGroup';
import { ProductionEquipment } from './productionEquipment';
import { ProductionQuantity } from './ProductionQuantity';
import { ProductionText } from './ProductionText';
import { BillingOverviewConfig } from './table-configs/billing-tables.config';
import { TimesheetHandler } from './timesheetHandler';

export class BillingTimesheet extends TimesheetHandler {
  public contractWrapper: ContractHandler;
  public override isPayroll = false;
  public sharepointFiles: ISharepointDisplayData[] = [];
  public productionGroups: ProductionGroup[] = [];
  public contractCustGrp: string;
  public contractCostCenter: string;

  private readonly readySubject$ = new BehaviorSubject(false);
  public readonly ready$ = this.readySubject$.asObservable();

  private currentTimesheetNumber: string;
  private readonly equipmentReadySubject$ = new BehaviorSubject(false);
  private readonly employeesReadySubject$ = new BehaviorSubject(false);
  private readonly contractReadySubject$ = new BehaviorSubject(false);
  private readonly extDocumentsReadySubject$ = new BehaviorSubject(false);
  private readonly sharepointFilesReadySubject$ = new BehaviorSubject(false);

  constructor(datahub: DatahubService, modalService: ModalService) {
    super(datahub, modalService);
    combineLatest([
      this.equipmentReadySubject$,
      this.employeesReadySubject$,
      this.contractReadySubject$,
      this.extDocumentsReadySubject$,
      this.sharepointFilesReadySubject$,
    ]).subscribe({
      next: (values) => {
        const isAllReady = values.every((isValReady) => isValReady); //once employees, production, equipment and timesheets are ready, we can proceed
        if (!isAllReady) return null;
        this.sort();
        this.determineTimesheetStatus();
        this.readySubject$.next(true);
      },
      error: (err) => {
        console.error(err);
      },
    });
    this.display = `Billing Timesheet Summary`;
  }

  public setServiceOrder$(serviceOrder: string): Observable<ContractHandler> {
    return this.datahub.contracts.getNewServiceOrder$(serviceOrder, this.weDate.getFilterString()).pipe(
      filter((contract) => contract != null),
      tap((contract) => {
        this.contractWrapper = contract;
        this.contractCustGrp = this.contractWrapper.getCustomerGroup();
        this.contractCostCenter = this.contractWrapper.getCostCenter();
      }),
    );
  }

  public processTimesheetChange(timesheetNumber: string): void {
    this.employees.forEach((x) => {
      x.filterByTimesheet(timesheetNumber);
    });
    this.equipment.forEach((x) => {
      x.filterByTimesheet(timesheetNumber);
    });
    this.productionQuantity.forEach((x) => {
      x.filterByTimesheet(timesheetNumber);
    });
    this.productionEquipment.forEach((x) => {
      x.filterByTimesheet(timesheetNumber);
    });
    this.productionText.forEach((x) => {
      x.filterByTimesheet(timesheetNumber);
    });
    this.productionGroups.forEach((x) => {
      x.filterByTimesheet(timesheetNumber);
    });
    this.displaySharepointFilesByTimesheet(trimLeadingZeros(timesheetNumber), this.sharepointFiles);
    if (timesheetNumber === '') this.applicableExtDocuments = this.extDocuments;
    else {
      this.currentTimesheetNumber = timesheetNumber;
      const foundDocument = this.extDocuments.find((x) => trimLeadingZeros(x.Documentno) === trimLeadingZeros(timesheetNumber));
      this.applicableExtDocuments = foundDocument ? [foundDocument] : [];
    }
  }

  public navigateToSelf(approver = this.datahub.user.approvingId): void {
    const url = `/billing/review?order=${this.contractWrapper.value.ServiceOrder}&wedate=${this.weDate.getSapWeDateString()}&approver=${approver}`;
    this.datahub.navigate(url);
  }

  private getRecords(shouldFilterApprovers: boolean): void {
    if (!this.contractWrapper) {
      this.datahub.messageService.error(
        'The service order is no longer valid, but there may be timesheets that are still referencing it. This page cannot be loaded.',
        'Invalid Service Order',
      );
      return;
    }
    this.readySubject$.next(false);
    this.equipmentReadySubject$.next(false);
    this.employeesReadySubject$.next(false);
    this.extDocumentsReadySubject$.next(false);
    this.sharepointFilesReadySubject$.next(false);
    this.extDocuments = [];
    this.applicableExtDocuments = [];
    const weDate = this.weDate.selectedDate.value.format(DATE_FORMAT_SAP);

    this.findUserExtDocuments$(shouldFilterApprovers)
      .pipe(
        switchMap(() => {
          if (!shouldFilterApprovers) {
            return this.datahub.catsDB.getBillingRecords$(this.contractWrapper.value.ServiceOrder, weDate);
          } else if (arrayIsTruthy(this.userExtDocuments)) {
            return this.datahub.catsDB.getBillingRecords$(this.contractWrapper.value.ServiceOrder, weDate, this.userExtDocuments);
          } else {
            //if there are no documents for the user, then for billing, return nothing
            return of(this.datahub.catsDB.makeResultsObject([]));
          }
        }),
        tap((values) => {
          if (!arrayIsTruthy(this.userExtDocuments)) {
            this.handleNoTimesheets();
            return;
          }
          this.employeeRecords = values.employees;
          this.equipmentRecords = values.equipment;
          this.productionQuantityRecords = values.productionQuantity;
          this.productionTextRecords = values.productionText;
          this.catsRecords = values.all; //assign records appropriately
          this.determineExtDocuments(shouldFilterApprovers);
          this.determineEmployees(); //need to figure out which employees belong on a timesheet by deconstructing them from the records
          this.determineEquipment(); //need to figure out which equipment belong on a timesheet by deconstruction
        }),
        switchMap(() => this.contractWrapper.__skfsReady$),
        tap((isReady) => {
          if (!isReady) return;
          this.getProduction(); //find production entities from the contract
          this.assignProductionQuantityRecords(); //assign the correct records to the entities
          this.assignProductionTextRecords(); //assign the correct records to the entities
          this.assignProductionEquipmentRecords();
          this.productionGroups = this.assignProductionGroups(this.catsRecords);
          this.contractReadySubject$.next(isReady); //now production is finished too
          this.getAllSharepointFiles();
        }),
      )
      .subscribe({
        error: (err) => {
          console.error('Error in observable chain:', err);
        },
      });
  }

  public checkUnassignedRecords(): boolean {
    //If the summary contains any items without a billcode or any flagged items, return false
    // See the `makeOverviewRow` method for what determines if something is flagged or not
    return this.getValues().some((row) => row.shouldFlag && row.description === CatsNoBillCodeDescriptionEnum.Unassigned);
  }

  public checkNoContractRecords(): boolean {
    return this.getValues().some((row) => row.shouldFlag && row.description === CatsNoBillCodeDescriptionEnum.NotOnContract);
  }

  public checkApprovedPayroll(): boolean {
    const payrollStatus = this.determineTimesheetStatus(); //if there are unapproved payroll records, intercept  approval
    return payrollStatus !== TimesheetStatusEnum.InProgress;
  }

  private handleNoTimesheets(): void {
    this.modalService
      .launchResponsiveModal('simple-text', {
        title: 'You have no timesheets for this week and this contract',
        body: 'Click ok to go to timesheet manager',
      })
      .afterClosed()
      .subscribe({
        next: (bool) => {
          if (bool) {
            this.datahub.navigate('/timesheetmanager');
            return;
          }
          if (!bool) {
            this.datahub.navigate('/home');
          }
        },
        error: (err) => {
          console.error('Error in handleNoTimesheets:', err);
        },
      });
  }

  public async handleUnapprovedCase(): Promise<void> {
    const shouldViewPayroll = await this.modalService.launchConfirmModal({
      title: 'This contract has unapproved payroll hours',
      questionText: 'Payroll needs to be approved before continuing',
      yesText: 'View Payroll',
      noText: 'Cancel',
    });

    if (shouldViewPayroll) {
      this.datahub.billingTimesheet.goToUnapprovedPayroll();
    }
  }

  public handleNoHoursCase(): void {
    this.modalService.launchResponsiveModal('simple-text', {
      title: 'No Time to Approve',
      body: 'This page has no timesheets with any records.',
      buttonText: 'Go Back',
    });
  }

  public goToUnapprovedPayroll(): void {
    this.datahub.navigate(`/payroll?wedate=${this.weDate.getSapWeDateString()}`);
  }

  private determineExtDocuments(shouldFilterApprovers: boolean): void {
    if (shouldFilterApprovers) {
      this.extDocuments = this.userExtDocuments.filter(
        (x) => x.Salesdoc === this.contractWrapper.getServiceOrder(), //for billing, we only want timesheets that share a service order.
      );
      this.extDocumentsReadySubject$.next(true);
    } else {
      this.findExtDocuments$().subscribe({
        next: (x) => {
          if (x) {
            this.extDocumentsReadySubject$.next(true);
          }
        },
        error: (err) => {
          console.error('Error in determineExtDocuments:', err);
        },
      });
    }
  }

  private determineEquipment(): void {
    const ids = findUnique<string, ICatRecord>(this.equipmentRecords, 'Zequipment');
    if (!arrayIsTruthy(ids)) {
      this.equipmentReadySubject$.next(true);
      return;
    }
    this.datahub.apiService.getEquipment$({ EquipmentId: ids, ValidTo: this.weDate.getFilterString() }).subscribe({
      next: (employees) => {
        this.equipment = employees.map((x) => {
          const eqp = new Equipment(x, this.datahub, 'billing');
          eqp.setDefaultCode(this.contractWrapper);
          return eqp;
        });
        this.assignEquipmentRecords();
        this.equipmentReadySubject$.next(true);
      },
      error: (err) => {
        console.error('Error in determineEquipment:', err);
      },
    });
  }

  private determineEmployees(): void {
    const ids = findUnique<string, ICatRecord>(this.employeeRecords, 'Employeenumber');
    if (!arrayIsTruthy(ids)) {
      this.employeesReadySubject$.next(true);
      return;
    }
    this.datahub.apiService.getEmployees$({ EmpId: ids, WeDate: this.weDate.getFilterString() }).subscribe({
      next: (employees) => {
        this.employees = employees.map((x) => new Employee(x, this.datahub, this.modalService, 'billing'));
        this.assignEmployeeRecords();
        this.employeesReadySubject$.next(true);
      },
      error: (err) => {
        console.error('Error in determineEmployees:', err);
      },
    });
  }

  private getProduction(): void {
    this.productionQuantity = this.contractWrapper.__quantitySKF.map((x) => new ProductionQuantity(this.datahub, x, 'billing'));
    this.productionText = this.contractWrapper.__textSKF.map((x) => new ProductionText(this.datahub, x, 'billing'));
    this.productionEquipment = this.contractWrapper.__equipmentSKF.map(
      (x) => new ProductionEquipment(this.datahub, x, 'billing'),
    );
  }

  private assignProductionQuantityRecords(): void {
    this.productionQuantity.forEach((quantity) => {
      const records = this.productionQuantityRecords.filter((record) => record.Statkeyfig === quantity.SKF);
      quantity.setCatsRecords(records);
    });
  }

  private assignProductionTextRecords(): void {
    this.productionText.forEach((text) => {
      const records = this.productionTextRecords.filter((record) => record.Statkeyfig === text.SKF);
      text.setCatsRecords(records);
    });
  }

  private assignProductionEquipmentRecords(): void {
    this.productionEquipment.forEach((entity) => {
      const records = this.productionQuantityRecords.filter((record) => record.Statkeyfig === entity.SKF);
      entity.setCatsRecords(records);
    });
  }

  public override assignEquipmentRecords(): void {
    //find cats records in search belonging to equipment. MaKe records into array by each unique equipment number
    this.equipment.forEach((equipment) => {
      const records = this.equipmentRecords.filter((record) => safeCompare(equipment.EquipmentId, record.Zequipment));
      equipment.setCatsRecords(records); //basic interface classes each have the ability to perform separate required logic by type.
    });
  }

  private assignProductionGroups(catsRecords: ICatRecord[]): ProductionGroup[] {
    const filteredRecords = catsRecords.filter((x) => Number(x.Quantity) > 0 && !!x.ZprdGrpID); //remove nullish values
    const groups = findUnique<string>(filteredRecords, 'ZprdGrpID');
    const config = this.contractWrapper.conditions.prdGroup;
    const acting = this.datahub.user.approvingId;
    const user = this.datahub.user.getEmpId();
    const recOrder = this.contractWrapper.getServiceOrder();
    return groups.map((id) => {
      const records = filteredRecords.filter((x) => x.ZprdGrpID === id);

      const firstRecord = records[0];
      const date = firstRecord.Workdate;
      const documentno = firstRecord.Extdocumentno;
      return new ProductionGroup(config, acting, user, recOrder, documentno, id, date, records, this.datahub.messageService);
    });
  }

  public addProductionGroup$(date: string, extDocNo: string): Observable<ProductionGroup> {
    return this.datahub.apiService.getProductionGroupId$().pipe(
      map((id) => {
        const documentno = extDocNo;
        const config = this.contractWrapper.conditions.prdGroup;
        const acting = this.datahub.user.approvingId;
        const user = this.datahub.user.getEmpId();
        const recOrder = this.contractWrapper.getServiceOrder();
        const group = new ProductionGroup(config, acting, user, recOrder, documentno, id, date, [], this.datahub.messageService);
        this.productionGroups.push(group);
        return group;
      }),
    );
  }

  public removeProductionGroup$(group: ProductionGroup): Observable<ProductionGroup[]> {
    return this.datahub.apiService.deleteProductionGroup$(group.id).pipe(
      map(() => {
        //Remove the group from the list after the HTTP request returns success
        this.productionGroups.splice(
          this.productionGroups.findIndex((y) => y.id === group.id),
          1,
        );
        this.updateExtDocumentStatus([group.ExtDocumentno]);
        return this.productionGroups;
      }),
    );
  }

  public reset(shouldFilterApprovers: boolean): void {
    this.employees = [];
    this.equipment = [];
    this.timesheetStatus = {
      number: '',
      description: 'Searching..',
      shouldPermitBillingApproval: false,
      shouldPermitPayrollApproval: false,
      error: '',
    };
    this.getRecords(shouldFilterApprovers);
  }

  public addDocument(entity: IAnyInterfaceType): void {
    //Don't run the find unless we know we are filtering...
    const singleDocument =
      entity.filteredTimesheet == null
        ? undefined
        : this.extDocuments.find((document) => safeCompare(document.Documentno, entity.filteredTimesheet));

    if (singleDocument) {
      // If we have a document match current timesheet, add it
      this.datahub.detailPayneHandler.addDocumentToView(singleDocument, entity.getBillingDetailPayneConfig());
      this.addExtDoc(singleDocument);
    } else {
      const data = {
        step: 2,
        contract: this.contractWrapper,
        type: 'billing',
        handler: this.weDate,
        timesheets: this.extDocuments,
      };
      this.modalService
        .launchResponsiveModal('timesheet-select', data)
        .afterClosed()
        .subscribe({
          next: (value: ExtDocument) => {
            if (!value) return;
            this.datahub.detailPayneHandler.addDocumentToView(value, entity.getBillingDetailPayneConfig());
            this.addExtDoc(value);
          },
          error: (err) => {
            console.error('Error in addDocument => afterClosed subscription:', err);
          },
        });
    }
  }

  public getConfig(): ITableConfigEntity[] {
    return BillingOverviewConfig;
  }

  public getValues(): IBillingSummaryRow[] {
    let records: ICatRecord[] = []; //we do it this way, instead of just getting all the records on the timesheet so it can adapt without refreshing.
    records = records.concat(this.findCatsRecords(this.employees)); //find all records in employees
    records = records.concat(this.findCatsRecords(this.equipment)); //find all records in equipment
    records = records.concat(this.findCatsRecords(this.productionQuantity)); //find all records in production Quantity
    records = records.concat(this.findCatsRecords(this.productionEquipment)); //find all records in production equipment
    const codes = findUnique<string>(records, 'Zmatnr', true);
    const rows = codes.map((code) => {
      return this.makeOverviewRow(code, records);
    });

    //Sort the flagged rows first in the array so they are sure to be noticed.
    //Anything in the flagged rows are sorted alphabetically by the description
    let sortedRows = [
      ...rows.filter((x) => x.shouldFlag).sort((a, b) => a.description.localeCompare(b.description)),
      ...rows.filter((x) => !x.shouldFlag).sort((a, b) => a.description.localeCompare(b.description)),
    ];

    //Only return the rows that have a total which evaluates to a boolean when the type is coerced
    sortedRows = sortedRows.filter((x) => Number(x.total));
    const total: string = rows.reduce((x, y) => addDecimals(x, y.total), '0');
    sortedRows.push({
      Zmatnr: '',
      description: 'Total',
      total: total,
      shouldFlag: false,
      hasNoBillCode: false,
    });
    return sortedRows;
  }

  private makeOverviewRow(code: string, records: ICatRecord[]): IBillingSummaryRow {
    let total = '0';
    const hasNoBillCode = !code;
    const relevant = records.filter((x) => x.Zmatnr === code);
    const description = this.contractWrapper.getBillCodeDescription(code);
    const shouldFlag =
      hasNoBillCode ||
      description === CatsNoBillCodeDescriptionEnum.NotOnContract ||
      description === CatsNoBillCodeDescriptionEnum.Unassigned;

    function getValue(record: ICatRecord): number {
      const catsHours = Number(record.Catshours ?? 0);
      const quantity = Number(record.Quantity ?? 0);
      if (catsHours) return catsHours;
      if (quantity) return quantity;
      return 0;
    }

    //the decimal numbers are a string, this adds everything up as string/decimals and gets a total
    relevant.forEach((element) => {
      const value = getValue(element);
      total = addDecimals(total, value);
    });

    return {
      Zmatnr: code,
      description,
      total,
      shouldFlag, //used by TableLikeLayout to display the row in the UI differently to draw attention
      hasNoBillCode: hasNoBillCode,
    };
  }

  public updateExtDocumentStatus(timesheets: string[]): void {
    //with billing, any change reverts the timesheet back to PA unless is was in IP. Its unlikely, but an initialized timesheet with changes in billing screen set the timesheet to IP.
    timesheets.forEach((ts) => {
      const document = this.extDocuments.find((y) => safeCompare(y.Documentno, ts));
      if (document != null) {
        if (document.Status === TimesheetStatusEnum.BillingApproved) {
          document.updateSelf(TimesheetStatusEnum.PayrollApproved);
        } else if (document.Status === TimesheetStatusEnum.Created) {
          document.updateSelf(TimesheetStatusEnum.InProgress);
        }
      }
    });
    this.determineTimesheetStatus();
  }

  public updateSharepointFile(weekendingDate: string, selectedSharepointRow: ISharepointDisplayData): void {
    const timesheetNo = selectedSharepointRow.timesheetNo;

    //Display name is either a string or a string array. We need it to become a string in all cases
    const displayName =
      typeof selectedSharepointRow.displayName === 'string'
        ? selectedSharepointRow.displayName //just us the string name
        : selectedSharepointRow.displayName.join(''); //join all array parts together if the display name is an array

    let newName = timesheetNo
      ? `${timesheetNo}.${displayName}${selectedSharepointRow.fileType}`
      : displayName + selectedSharepointRow.fileType;

    const oldName = selectedSharepointRow.fileName;
    const currentSharepointFile = this.sharepointFiles.find(
      (csf) => csf.fileName.toLowerCase() !== newName.toLowerCase() && csf.fileName.toLowerCase() === oldName.toLowerCase(),
    );

    if (currentSharepointFile) {
      const costCenter = this.contractCostCenter;
      const custGrp = this.contractCustGrp;
      const doesNameExist = this.sharepointFiles.some((csf) => csf.fileName.toLowerCase() === newName.toLowerCase());
      const invalidSharepointCharsRegex = /[*.:<>?/\\|]/;
      const hasInvalidSharepointCharacter =
        typeof selectedSharepointRow.displayName === 'string' &&
        invalidSharepointCharsRegex.test(selectedSharepointRow.displayName);
      const replacementFileName = timesheetNo
        ? currentSharepointFile.fileName.split('.').slice(1, -1)
        : currentSharepointFile.fileName.split('.').slice(0, -1);

      //Is the name the same as before, and does the name already exist on the timesheet
      if (doesNameExist) {
        selectedSharepointRow.displayName = replacementFileName;
        newName = currentSharepointFile.fileName;
        this.datahub.messageService.error('Please choose a different file name', 'Name already exists');
      }

      if (hasInvalidSharepointCharacter) {
        selectedSharepointRow.displayName = replacementFileName;
        newName = currentSharepointFile.fileName;
        this.datahub.messageService.error(
          'Please enter a name that does not have the following characters: <code class="p-1 bg-black/10">"*:<>?/|.</code>',
          'File Name Contains Invalid characters',
        );
      }

      this.datahub.apiService.updateSharepointFile$(costCenter, weekendingDate, custGrp, oldName, newName).subscribe({
        next: (sp) => {
          if (!this.currentTimesheetNumber) this.currentTimesheetNumber = selectedSharepointRow.timesheetNo.padStart(7, '0');
          this.updateExtDocumentStatus([this.currentTimesheetNumber]);
          currentSharepointFile.fileName = newName;
          currentSharepointFile.hasBeenUploaded = true;
          currentSharepointFile.canDelete = true;
          currentSharepointFile.canView = true;
          currentSharepointFile.url = sp.webUrl;
        },
        error: () => {
          this.datahub.messageService.error('There was an issue updating the file. Please try again.', 'Error');
        },
      });
    } else {
      selectedSharepointRow.canDelete = true;
      selectedSharepointRow.hasBeenUploaded = true;
      selectedSharepointRow.canView = true;
    }
  }

  public deleteSharepointFile(weekendingDate: string, sharepointEntity: ISharepointDisplayData): Subscription {
    this.updateExtDocumentStatus([sharepointEntity.timesheetNo]);
    this.datahub.billingTimesheet.determineTimesheetStatus();
    const custGrp = this.contractCustGrp;
    const costCenter = this.contractCostCenter;
    this.sharepointFiles.map((file, i) => {
      if (file.fileName === sharepointEntity.fileName) this.sharepointFiles.splice(i, 1);
    });
    return this.datahub.apiService
      .deleteSharepointFiles$(costCenter, weekendingDate, custGrp, sharepointEntity.fileName)
      .subscribe({
        error: (err) => {
          console.error(err);
        },
      });
  }

  public addSharepointFile(fileName: string, displayName: string[], fileType: string, timesheetNo: string): void {
    this.updateExtDocumentStatus([timesheetNo]);
    const displayObject: ISharepointDisplayData = {
      displayName,
      fileName,
      fileType,
      timesheetNo,
      canEdit: true,
      hasBeenUploaded: false,
      url: '',
      weekendingDate: '',
    };
    this.sharepointFiles.push(displayObject);
  }

  private showAllSharepointFiles(files: ISharepointDisplayData[]): void {
    files.forEach((file) => {
      if (file.shouldNeverShow) return;

      file.shouldShow = true;
    });
  }

  private displaySharepointFilesByTimesheet(timesheetNumber: string, displayFiles: ISharepointDisplayData[]): void {
    this.showAllSharepointFiles(displayFiles);
    if (!timesheetNumber) return;
    displayFiles.map((spf) => {
      if (spf.timesheetNo !== timesheetNumber && spf.timesheetNo) spf.shouldShow = false;
    });
  }

  private getAllSharepointFiles(): void {
    const weekendingDate = this.weDate.selectedDate.value.format(DATE_FORMAT_SHAREPOINT);
    const costCenter = this.contractCostCenter;
    const custGrp = this.contractCustGrp;

    this.datahub.apiService.getSharepointFiles$(costCenter, weekendingDate, custGrp).subscribe({
      next: (res) => {
        const sp: ISharepointDisplayData[] = res.map((item) => {
          const wasUploadedFromSharepoint = item.Name.split('.').length === 2;
          const timesheetNo = wasUploadedFromSharepoint ? '' : trimLeadingZeros(item.Name.split('.').shift() ?? '');
          const relevantTimesheetNumbers = this.extDocuments.filter(
            (document) => document.Salesdoc === this.contractWrapper.getServiceOrder(),
          );
          // Not a fan of this, but I need a way to show timesheets that match the tabs above 'activeTimesheets'
          // Then another way that I can disable the delete and edit options. If I were
          // to combine them, then when viewing whole contract, you wouldn't see timesheets not owned by you
          const hasActiveTimesheets = relevantTimesheetNumbers.some((x) => x.Documentno === timesheetNo.padStart(7, '0'));
          const isOwnedByApprover = relevantTimesheetNumbers.some(
            (x) => x.Documentno === timesheetNo.padStart(7, '0') && x.Owner === this.datahub.user.getApprover(),
          );
          const rawDisplayName = item.Name.split('.')
            .slice(wasUploadedFromSharepoint ? 0 : 1, -1)
            .join();
          return {
            fileName: item.Name,
            timesheetNo,
            displayName: trimLeadingZeros(rawDisplayName),
            url: item.ServerRelativeUrl,
            fileType: `.${item.ListItemAllFields.FieldValuesAsText.File_x005f_x0020_x005f_Type}`,
            weekendingDate: moment(item.TimeCreated).format('MM-DD-YY'),
            canDelete: isOwnedByApprover,
            canView: true,
            canEdit: !isOwnedByApprover,
            hasBeenUploaded: true,
            shouldShow: true,
            neverShow: item.ListItemAllFields.FieldValuesAsText.Editor === 'daveytime sposervice' || !hasActiveTimesheets,
          };
        });

        this.sharepointFiles = sp.sort((a, b) => {
          if (a.timesheetNo < b.timesheetNo) return 1;
          return b.timesheetNo < a.timesheetNo ? -1 : 0;
        });
        this.sharepointFilesReadySubject$.next(true);
      },
      error: (error) => {
        console.error(error);
        this.sharepointFilesReadySubject$.next(true);
      },
    });
  }

  public override findUserExtDocuments$(shouldFilterApprovers = false): Observable<IExtDocument[]> {
    let params;
    if (shouldFilterApprovers) {
      params = {
        Dateentered: this.weDate.getFilterString(),
        Owner: this.datahub.user.getApprover(),
        Salesdoc: this.contractWrapper.value.ServiceOrder,
      };
    } else {
      params = {
        Dateentered: this.weDate.getFilterString(),
        Salesdoc: this.contractWrapper.value.ServiceOrder,
      };
    }
    return this.datahub.apiService.getTimesheets$(params).pipe(
      tap((documents) => {
        this.userExtDocuments = documents.map((x) => new ExtDocument(x, this.datahub, false, this.contractWrapper));
        this.applicableExtDocuments = this.userExtDocuments;
        this.extDocuments = this.userExtDocuments;
      }),
    );
  }

  private findCatsRecords(array: BasicInterface[]): ICatRecord[] {
    //NOTE: The types are not correct here, but this is how it has been working
    //There is `CatRecord` and there is `catsRecord` which are not the same thing!
    if (!array) return [];
    //each record type has a method called 'getCatsRecords' that will return the appropriate properties for each record.
    const records: ICatRecord[][] = [];
    array.forEach((element) => {
      const recordArr = element.getCatRecords();
      if (recordArr) records.push(recordArr);
    });
    return records.flat();
  }
}

export interface IBillingSummaryRow {
  Zmatnr: string;
  description: string;
  total: string;
  shouldFlag: boolean; // if any are true, this cannot be approved, also used by TableLikeLayout to display the row in the UI differently to draw attention
  hasNoBillCode: boolean; // if any are true, this cannot be approved
}

export interface ISharepointDisplayData {
  canDelete?: boolean;
  canView?: boolean;
  displayName: string | string[];
  fileName: string;
  fileType: string;
  shouldNeverShow?: boolean;
  oldName?: string;
  canEdit?: boolean;
  shouldHighlight?: boolean;
  shouldShow?: boolean;
  timesheetNo: string;
  hasBeenUploaded: boolean;
  url: string;
  weekendingDate: string;
}
